import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc, collection, query, where, getDocs, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";

const FLAGS = {
  "Mexico": "🇲🇽", "South Africa": "🇿🇦", "South Korea": "🇰🇷",
  "Czech Republic": "🇨🇿", "Canada": "🇨🇦", "Switzerland": "🇨🇭",
  "Qatar": "🇶🇦", "Bosnia & Herzegovina": "🇧🇦", "Brazil": "🇧🇷",
  "Morocco": "🇲🇦", "Scotland": "🏴󠁧󠁢󠁳󠁣󠁴󠁿", "Haiti": "🇭🇹",
  "USA": "🇺🇸", "Paraguay": "🇵🇾", "Australia": "🇦🇺", "Turkey": "🇹🇷",
  "Germany": "🇩🇪", "Ivory Coast": "🇨🇮", "Ecuador": "🇪🇨", "Curaçao": "🇨🇼",
  "Netherlands": "🇳🇱", "Tunisia": "🇹🇳", "Japan": "🇯🇵", "Sweden": "🇸🇪",
  "Belgium": "🇧🇪", "Egypt": "🇪🇬", "Iran": "🇮🇷", "New Zealand": "🇳🇿",
  "Spain": "🇪🇸", "Cape Verde": "🇨🇻", "Saudi Arabia": "🇸🇦", "Uruguay": "🇺🇾",
  "France": "🇫🇷", "Senegal": "🇸🇳", "Norway": "🇳🇴", "Iraq": "🇮🇶",
  "Argentina": "🇦🇷", "Algeria": "🇩🇿", "Austria": "🇦🇹", "Jordan": "🇯🇴",
  "Portugal": "🇵🇹", "Colombia": "🇨🇴", "Uzbekistan": "🇺🇿", "DR Congo": "🇨🇩",
  "England": "🏴󠁧󠁢󠁥󠁮󠁧󠁿", "Croatia": "🇭🇷", "Ghana": "🇬🇭", "Panama": "🇵🇦",
};

function getFlag(team) {
  return FLAGS[team] || "🏳️";
}

export default function League() {
  const { leagueId } = useParams();
  const { currentUser } = useAuth();
  const [league, setLeague] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [pendingDetails, setPendingDetails] = useState([]);
  const [activeTab, setActiveTab] = useState("leaderboard");

  // Predictions tab state
  const [completedMatches, setCompletedMatches] = useState([]);
  const [allPredictions, setAllPredictions] = useState({});
  const [membersData, setMembersData] = useState([]);

  useEffect(() => {
    async function fetchLeague() {
      const leagueSnap = await getDoc(doc(db, "leagues", leagueId));
      if (!leagueSnap.exists()) return;
      const leagueData = { id: leagueSnap.id, ...leagueSnap.data() };

      // Block non-members from private leagues
      const ADMIN_EMAIL = "jonnybroughton@me.com";
      if (!leagueData.isPublic && !leagueData.members?.includes(currentUser.uid) && currentUser.email !== ADMIN_EMAIL) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      setLeague(leagueData);

      // Fetch pending join requests if creator
      if (leagueData.createdBy === currentUser.uid && leagueData.pendingMembers?.length > 0) {
        const pending = await Promise.all(
          leagueData.pendingMembers.map(async (uid) => {
            const userSnap = await getDoc(doc(db, "users", uid));
            return {
              uid,
              displayName: userSnap.exists()
                ? userSnap.data().displayName || userSnap.data().email
                : uid,
            };
          })
        );
        setPendingMembers(leagueData.pendingMembers);
        setPendingDetails(pending);
      }

      // For public league fetch all users, for private use members array
      let members = leagueData.members || [];
      if (leagueData.isPublic) {
        const usersSnap = await getDocs(collection(db, "users"));
        members = usersSnap.docs.map((d) => d.id);
      }

      // Fetch all matches
      const matchesSnap = await getDocs(collection(db, "matches"));
      const matchMap = {};
      matchesSnap.docs.forEach((d) => { matchMap[d.id] = d.data(); });

      // Fetch completed matches for predictions tab
      const completed = matchesSnap.docs
        .filter((d) => d.data().status === "completed")
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => new Date(a.kickoff) - new Date(b.kickoff));
      setCompletedMatches(completed);

      // Fetch predictions in batches of 30
      let allPreds = [];
      for (let i = 0; i < members.length; i += 30) {
        const batch = members.slice(i, i + 30);
        const predsSnap = await getDocs(
          query(collection(db, "predictions"), where("userId", "in", batch))
        );
        allPreds = [...allPreds, ...predsSnap.docs.map((d) => d.data())];
      }

      // Build predictions map per member per match for predictions tab
      const predsByMember = {};
      allPreds.forEach((pred) => {
        if (!predsByMember[pred.userId]) predsByMember[pred.userId] = {};
        predsByMember[pred.userId][pred.matchId] = pred;
      });
      setAllPredictions(predsByMember);

      // Build stats per member
      const memberStats = {};
      members.forEach((uid) => {
        memberStats[uid] = {
          totalPoints: 0,
          correctScores: 0,
          correctWinners: 0,
          correctGoalscorers: 0,
        };
      });

      allPreds.forEach((pred) => {
        const uid = pred.userId;
        const match = matchMap[pred.matchId];
        if (!memberStats[uid] || !match || match.status !== "completed") return;

        const pts = pred.pointsEarned || 0;
        memberStats[uid].totalPoints += pts;

        const [actualHome, actualAway] = (match.result || "").split("-").map(Number);
        if (pred.homeScore === actualHome && pred.awayScore === actualAway) {
          memberStats[uid].correctScores += 1;
        } else if (pts >= 2) {
          memberStats[uid].correctWinners += 1;
        }

        const predGoal = (pred.goalscorer || "").toLowerCase().trim();
        const actualGoal = (match.firstGoalScorer || "").toLowerCase().trim();
        if (predGoal && actualGoal && predGoal === actualGoal) {
          memberStats[uid].correctGoalscorers += 1;
        }
      });

      // Fetch display names and build leaderboard
      const board = await Promise.all(
        members.map(async (uid) => {
          const userSnap = await getDoc(doc(db, "users", uid));
          const displayName = userSnap.exists()
            ? userSnap.data().displayName || userSnap.data().email
            : uid;
          return { uid, displayName, ...memberStats[uid] };
        })
      );

      board.sort((a, b) => b.totalPoints - a.totalPoints);
      setLeaderboard(board);
      setMembersData(board);
      setLoading(false);
    }
    fetchLeague();
  }, [leagueId, currentUser]);

  async function handleApprove(uid) {
    await updateDoc(doc(db, "leagues", leagueId), {
      members: arrayUnion(uid),
      pendingMembers: arrayRemove(uid),
    });
    setPendingDetails((prev) => prev.filter((m) => m.uid !== uid));
    setPendingMembers((prev) => prev.filter((id) => id !== uid));
  }

  async function handleReject(uid) {
    await updateDoc(doc(db, "leagues", leagueId), {
      pendingMembers: arrayRemove(uid),
    });
    setPendingDetails((prev) => prev.filter((m) => m.uid !== uid));
    setPendingMembers((prev) => prev.filter((id) => id !== uid));
  }

  function copyInviteLink() {
    navigator.clipboard.writeText(`${window.location.origin}/join/${league.inviteCode}`);
    setInviteCopied(true);
    setTimeout(() => setInviteCopied(false), 2000);
  }

  function copyInviteCode() {
    navigator.clipboard.writeText(league.inviteCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  }

  const medalEmoji = ["🥇", "🥈", "🥉"];

  if (accessDenied) return (
    <div className="min-h-screen" style={{ backgroundColor: "#F0F1EC" }}>
      <Navbar />
      <div className="max-w-lg mx-auto px-6 py-20 text-center">
        <div className="card p-10">
          <div className="font-display text-6xl mb-4" style={{ color: "#E0E2DB" }}>🔒</div>
          <h2 className="font-display text-4xl tracking-wider mb-3" style={{ color: "#0A0B0F" }}>PRIVATE LEAGUE</h2>
          <p className="font-condensed text-gray-400 tracking-wide text-lg mb-6">
            You need an invite code to access this league.
          </p>
          <Link to="/leagues" className="btn-lime px-8 py-3 rounded-xl inline-block">BACK TO LEAGUES</Link>
        </div>
      </div>
    </div>
  );

  if (loading) return (
    <div className="min-h-screen" style={{ backgroundColor: "#F0F1EC" }}>
      <Navbar />
      <p className="font-condensed text-gray-400 p-10 text-xl">Loading league...</p>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F0F1EC" }}>
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-10">

        {/* Header */}
        <div className="mb-8 fade-up">
          <p className="font-condensed text-gray-500 tracking-widest uppercase text-sm mb-2">League</p>
          <h2 className="font-display text-5xl md:text-7xl tracking-wider leading-none" style={{ color: "#0A0B0F" }}>
            {league.name.toUpperCase()}
          </h2>
          <p className="font-condensed text-gray-400 tracking-wide mt-2">
            {league.isPublic ? "Global public league" : `${league.members?.length} member${league.members?.length !== 1 ? "s" : ""}`}
          </p>
        </div>

        {/* Invite buttons */}
        {!league.isPublic && (
          <div className="flex flex-wrap gap-3 mb-8 fade-up fade-up-delay-1">
            <button
              onClick={copyInviteCode}
              className="font-condensed tracking-wide px-5 py-3 rounded-xl transition-all text-base"
              style={{
                backgroundColor: codeCopied ? "#EDFFD0" : "#FFFFFF",
                border: "1px solid #E0E2DB",
                color: codeCopied ? "#3A6600" : "#444",
                fontFamily: "monospace",
                letterSpacing: "0.1em",
              }}
            >
              {codeCopied ? "✓ Copied!" : `CODE: ${league.inviteCode}`}
            </button>
            <button onClick={copyInviteLink} className="btn-lime px-6 py-3 rounded-xl text-base">
              {inviteCopied ? "✓ LINK COPIED!" : "COPY INVITE LINK"}
            </button>
          </div>
        )}

        {/* Join Requests */}
        {league.createdBy === currentUser.uid && pendingDetails.length > 0 && (
          <div className="card overflow-hidden mb-6 fade-up" style={{ borderTop: "3px solid #FF5C00" }}>
            <div className="px-6 py-5" style={{ borderBottom: "1px solid #E0E2DB" }}>
              <div className="flex items-center gap-3">
                <h3 className="font-display text-2xl tracking-wider" style={{ color: "#0A0B0F" }}>JOIN REQUESTS</h3>
                <span className="font-condensed text-sm px-3 py-1 rounded-full"
                  style={{ backgroundColor: "#FFF3E0", color: "#B85C00" }}>
                  {pendingDetails.length} pending
                </span>
              </div>
              <p className="font-condensed text-gray-400 text-sm tracking-wide mt-1">
                Approve or reject requests to join your league
              </p>
            </div>
            {pendingDetails.map((member) => (
              <div key={member.uid} className="flex items-center justify-between px-6 py-4"
                style={{ borderBottom: "1px solid #E0E2DB" }}>
                <span className="font-condensed text-xl tracking-wide" style={{ color: "#0A0B0F" }}>
                  {member.displayName}
                </span>
                <div className="flex gap-3">
                  <button onClick={() => handleApprove(member.uid)} className="btn-lime px-5 py-2 rounded-lg text-sm">
                    APPROVE
                  </button>
                  <button onClick={() => handleReject(member.uid)}
                    className="font-condensed tracking-wide text-sm px-5 py-2 rounded-lg transition"
                    style={{ border: "1px solid #FFCCCC", color: "#CC0000", backgroundColor: "#FFF0F0" }}>
                    REJECT
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 fade-up">
          {["leaderboard", "predictions"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className="font-condensed tracking-wide px-5 py-2 rounded-xl text-base transition-all"
              style={{
                backgroundColor: activeTab === tab ? "#0A0B0F" : "#FFFFFF",
                color: activeTab === tab ? "#C8FF00" : "#888",
                border: activeTab === tab ? "none" : "1px solid #E0E2DB",
              }}
            >
              {tab === "leaderboard" ? "LEADERBOARD" : "PREDICTIONS"}
            </button>
          ))}
        </div>

        {/* ── LEADERBOARD TAB ── */}
        {activeTab === "leaderboard" && (
          <>
            <div className="card overflow-hidden fade-up fade-up-delay-2">

              {/* Table header — desktop */}
              <div className="px-6 py-4 hidden md:grid"
                style={{
                  borderBottom: "2px solid #E0E2DB",
                  backgroundColor: "#F8F9F5",
                  gridTemplateColumns: "2.5rem 1fr 7rem 7rem 7rem 6rem",
                }}>
                <div />
                <div className="font-condensed text-gray-400 tracking-widest text-xs uppercase">Player</div>
                <div className="font-condensed text-gray-400 tracking-widest text-xs uppercase text-center">⚽ Scorers</div>
                <div className="font-condensed text-gray-400 tracking-widest text-xs uppercase text-center">🎯 Scores</div>
                <div className="font-condensed text-gray-400 tracking-widest text-xs uppercase text-center">✅ Winners</div>
                <div className="font-condensed text-gray-400 tracking-widest text-xs uppercase text-right">Total</div>
              </div>

              {leaderboard.length === 0 && (
                <div className="px-6 py-12 text-center">
                  <p className="font-condensed text-gray-400 text-xl tracking-wide">
                    No predictions made yet — be the first!
                  </p>
                </div>
              )}

              {leaderboard.map((member, index) => {
                const isCurrentUser = member.uid === currentUser.uid;
                const isTop3 = index < 3;
                return (
                  <div key={member.uid}
                    style={{
                      borderBottom: "1px solid #E0E2DB",
                      borderLeft: isCurrentUser ? "4px solid #C8FF00" : "4px solid transparent",
                      backgroundColor: isCurrentUser ? "#FAFFF5" : "transparent",
                    }}>

                    {/* Desktop row */}
                    <div className="hidden md:grid items-center px-6 py-5"
                      style={{ gridTemplateColumns: "2.5rem 1fr 7rem 7rem 7rem 6rem" }}>
                      <div className="text-center">
                        {isTop3
                          ? <span className="text-2xl">{medalEmoji[index]}</span>
                          : <span className="font-condensed text-lg text-gray-400">{index + 1}</span>
                        }
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-condensed text-xl tracking-wide" style={{ color: "#0A0B0F" }}>
                          {member.displayName}
                        </span>
                        {isCurrentUser && (
                          <span className="font-condensed text-xs tracking-widest uppercase px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: "#EDFFD0", color: "#3A6600" }}>
                            you
                          </span>
                        )}
                      </div>
                      <div className="text-center">
                        <div className="font-display text-3xl" style={{ color: "#0A0B0F" }}>{member.correctGoalscorers}</div>
                      </div>
                      <div className="text-center">
                        <div className="font-display text-3xl" style={{ color: "#0A0B0F" }}>{member.correctScores}</div>
                      </div>
                      <div className="text-center">
                        <div className="font-display text-3xl" style={{ color: "#0A0B0F" }}>{member.correctWinners}</div>
                      </div>
                      <div className="text-right">
                        <span className="font-display text-3xl px-3 py-1 rounded-lg"
                          style={{ backgroundColor: isCurrentUser ? "#C8FF00" : "#F0F1EC", color: "#0A0B0F" }}>
                          {member.totalPoints}
                        </span>
                        <div className="font-condensed text-gray-400 text-xs tracking-widest uppercase mt-1">pts</div>
                      </div>
                    </div>

                    {/* Mobile row */}
                    <div className="md:hidden px-4 py-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-3">
                          <span>
                            {isTop3
                              ? <span className="text-xl">{medalEmoji[index]}</span>
                              : <span className="font-condensed text-gray-400">{index + 1}</span>
                            }
                          </span>
                          <div>
                            <span className="font-condensed text-lg tracking-wide" style={{ color: "#0A0B0F" }}>
                              {member.displayName}
                            </span>
                            {isCurrentUser && (
                              <span className="font-condensed text-xs tracking-widest uppercase ml-2 px-2 py-0.5 rounded-full"
                                style={{ backgroundColor: "#EDFFD0", color: "#3A6600" }}>
                                you
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="font-display text-3xl px-3 py-1 rounded-lg"
                          style={{ backgroundColor: isCurrentUser ? "#C8FF00" : "#F0F1EC", color: "#0A0B0F" }}>
                          {member.totalPoints}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { label: "⚽ Scorers", value: member.correctGoalscorers },
                          { label: "🎯 Scores", value: member.correctScores },
                          { label: "✅ Winners", value: member.correctWinners },
                        ].map((stat) => (
                          <div key={stat.label} className="text-center rounded-lg py-2"
                            style={{ backgroundColor: "#F5F6F2" }}>
                            <div className="font-display text-2xl" style={{ color: "#0A0B0F" }}>{stat.value}</div>
                            <div className="font-condensed text-gray-400 text-xs tracking-wide">{stat.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-4 md:gap-6 fade-up">
              {[
                { icon: "⚽", label: "First goalscorer correct" },
                { icon: "🎯", label: "Exact score correct" },
                { icon: "✅", label: "Match winner correct (no exact score)" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  <span>{item.icon}</span>
                  <span className="font-condensed text-gray-400 text-sm tracking-wide">{item.label}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── PREDICTIONS TAB ── */}
        {activeTab === "predictions" && (
          <div className="space-y-4 fade-up">

            {completedMatches.length === 0 && (
              <div className="card p-10 text-center">
                <div className="font-display text-5xl mb-4" style={{ color: "#E0E2DB" }}>⏳</div>
                <h3 className="font-display text-3xl tracking-wider mb-2" style={{ color: "#0A0B0F" }}>
                  NO RESULTS YET
                </h3>
                <p className="font-condensed text-gray-400 tracking-wide text-lg">
                  Predictions will appear here once matches are completed. Check back after June 11.
                </p>
              </div>
            )}

            {completedMatches.map((match) => (
              <div key={match.id} className="card overflow-hidden">

                {/* Match header */}
                <div className="px-4 md:px-6 py-4 flex items-center justify-between"
                  style={{ borderBottom: "1px solid #E0E2DB", backgroundColor: "#F8F9F5" }}>
                  <div className="min-w-0 flex-1">
                    <span className="font-condensed text-gray-400 text-xs tracking-widest uppercase">
                      Group {match.group} · {new Date(match.kickoff).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                    </span>
                    <div className="font-display tracking-wider mt-0.5 leading-tight"
                      style={{ color: "#0A0B0F", fontSize: "clamp(0.9rem, 3vw, 1.25rem)", wordBreak: "break-word" }}>
                      {getFlag(match.homeTeam)} {match.homeTeam} vs {match.awayTeam} {getFlag(match.awayTeam)}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-4">
                    <div className="font-display text-2xl tracking-wider" style={{ color: "#0A0B0F" }}>
                      {match.result}
                    </div>
                    {match.firstGoalScorer && (
                      <div className="font-condensed text-xs text-gray-400 tracking-wide mt-0.5">
                        ⚽ {match.firstGoalScorer}
                      </div>
                    )}
                  </div>
                </div>

                {/* Member predictions */}
                {membersData.map((member, index) => {
                  const pred = allPredictions[member.uid]?.[match.id];
                  const isCurrentUser = member.uid === currentUser.uid;
                  const isTop3 = index < 3;

                  return (
                    <div key={member.uid}
                      className="flex items-center justify-between px-4 md:px-6 py-3"
                      style={{
                        borderBottom: "1px solid #F5F6F2",
                        backgroundColor: isCurrentUser ? "#FAFFF5" : "transparent",
                        borderLeft: isCurrentUser ? "4px solid #C8FF00" : "4px solid transparent",
                      }}>

                      {/* Name + rank */}
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm flex-shrink-0">
                          {isTop3 ? medalEmoji[index] : <span className="font-condensed text-gray-300 text-xs">{index + 1}</span>}
                        </span>
                        <span className="font-condensed tracking-wide text-base truncate" style={{ color: "#0A0B0F" }}>
                          {member.displayName}
                        </span>
                        {isCurrentUser && (
                          <span className="font-condensed text-xs px-2 py-0.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: "#EDFFD0", color: "#3A6600" }}>
                            you
                          </span>
                        )}
                      </div>

                      {/* Prediction + points */}
                      {pred ? (
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          <div className="text-right">
                            <span className="font-condensed tracking-wide text-base" style={{ color: "#0A0B0F" }}>
                              {pred.homeScore}–{pred.awayScore}
                            </span>
                            {pred.goalscorer && (
                              <span className="font-condensed text-xs text-gray-400 ml-2 hidden md:inline">
                                ⚽ {pred.goalscorer}
                              </span>
                            )}
                          </div>
                          <span className="font-condensed text-sm font-bold px-2 py-1 rounded-lg flex-shrink-0"
                            style={{
                              backgroundColor: pred.pointsEarned >= 5 ? "#EDFFD0" : pred.pointsEarned >= 2 ? "#F0F4FF" : "#F5F6F2",
                              color: pred.pointsEarned >= 5 ? "#3A6600" : pred.pointsEarned >= 2 ? "#3B82F6" : "#999",
                            }}>
                            {pred.pointsEarned > 0 ? `+${pred.pointsEarned}` : "0"}
                          </span>
                        </div>
                      ) : (
                        <span className="font-condensed text-xs text-gray-300 tracking-wide flex-shrink-0 ml-2">
                          No prediction
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
